import { createSlice } from '@reduxjs/toolkit';

import initialState from './state';
import {
  fetchProjects,
  fetchProjectDetails,
  fetchProjectSettings,
  fetchWorkerProfileCustomFields,
  fetchAppLanguages,
  fetchCompanies,
  fetchEthnicities,
  fetchLanguages,
  fetchCountries,
  fetchStates,
  fetchIdTypes,
  fetchSkilledTrades,
  fetchCertificationTypes,
  fetchConsentFormFields,
  fetchConsentAgreements,
  fetchJobTitles,
  fetchTradeStatus,
  fetchSocJobTitles,
  fetchLanguageTurnerProtocols,
} from '../cache';
import { authenticateUser, authenticateUserInAdminSession, logoutUser, changePassword } from '../user';
import {
  createWorker,
  searchWorker,
  createPassword,
  acceptBiometricConsent,
  uploadImageToS3,
  uploadGovernmentDocumentFiles,
  deleteGovernmentDocumentFile,
  fetchWorkerDetails,
  updateWorker,
  addCertification,
  getCertificationDetails,
  updateCertification,
  deleteCertification,
  uploadSignatureToS3,
  acceptProject,
  fetchAllCertifications,
  fetchCertificationGroups,
  sendPinForPiiVerification,
  verifyPiiPin,
  assignExistingWorkerToCompanies,
  fetchWorkerConsentAcceptances,
} from '../onboarding';
import {
  fetchConsentFormInformation,
  downloadConsentFormPdf,
  fetchMyCertifications,
  fetchWorkerDrugTestingSchedules,
  addMyCertification,
  updateMyCertification,
  deleteMyCertification,
  deleteWorkerThirdPartyDrugTestResult,
  deleteWorkerDrugTestingSchedule,
  fetchProfileCustomFieldsConfig,
} from '../workerData';
import { resetAllLoaders, setIsFetchingData } from './actions';

import { actionMapForDataLoaders, actionMapForOtherLoaders, actionKeys } from '../../../constants';
import { loadingCaseGenerator } from '../utility';

const loadingMapSlice = createSlice({
  name: 'loader',
  initialState: initialState,
  reducers: {
    resetAllLoaders,
    setIsFetchingData,
  },
  extraReducers: builder =>
    loadingCaseGenerator(builder, [
      {
        thunkAction: authenticateUser,
        actionKey: actionMapForOtherLoaders.authenticateUser,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: authenticateUserInAdminSession,
        actionKey: actionMapForOtherLoaders.authenticateUserInAdminSession,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: logoutUser,
        actionKey: actionMapForOtherLoaders.logoutUser,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchProjects,
        actionKey: actionMapForDataLoaders.fetchProjects,
        isDataLoader: true,
      },
      {
        thunkAction: fetchProjectDetails,
        actionKey: actionMapForDataLoaders.fetchProjectDetails,
        isDataLoader: true,
      },
      {
        thunkAction: fetchProjectSettings,
        actionKey: actionMapForDataLoaders.fetchProjectSettings,
        isDataLoader: true,
      },
      {
        thunkAction: fetchWorkerProfileCustomFields,
        actionKey: actionMapForDataLoaders.fetchWorkerProfileCustomFields,
        isDataLoader: true,
      },
      {
        thunkAction: fetchAppLanguages,
        actionKey: actionKeys.fetchAppLanguages,
        isDataLoader: false,
      },
      {
        thunkAction: fetchCompanies,
        actionKey: actionKeys.fetchCompanies,
        isDataLoader: true,
      },
      {
        thunkAction: fetchEthnicities,
        actionKey: actionKeys.fetchEthnicities,
        isDataLoader: true,
      },
      {
        thunkAction: fetchLanguages,
        actionKey: actionKeys.fetchLanguages,
        isDataLoader: true,
      },
      {
        thunkAction: fetchCountries,
        actionKey: actionKeys.fetchCountries,
        isDataLoader: true,
      },
      {
        thunkAction: fetchStates,
        actionKey: actionKeys.fetchStates,
        isDataLoader: true,
      },
      {
        thunkAction: fetchIdTypes,
        actionKey: actionKeys.fetchIdTypes,
        isDataLoader: true,
      },
      {
        thunkAction: fetchSkilledTrades,
        actionKey: actionKeys.fetchSkilledTrades,
        isDataLoader: true,
      },
      {
        thunkAction: fetchCertificationTypes,
        actionKey: actionKeys.fetchCertificationTypes,
        isDataLoader: true,
      },
      {
        thunkAction: fetchConsentFormFields,
        actionKey: actionKeys.fetchConsentFormFields,
        isDataLoader: true,
      },
      {
        thunkAction: fetchConsentAgreements,
        actionKey: actionMapForDataLoaders.fetchConsentAgreements,
        isDataLoader: true,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchJobTitles,
        actionKey: actionKeys.fetchJobTitles,
        isDataLoader: true,
      },
      {
        thunkAction: fetchTradeStatus,
        actionKey: actionKeys.fetchTradeStatus,
        isDataLoader: true,
      },
      {
        thunkAction: fetchSocJobTitles,
        actionKey: actionKeys.fetchSocJobTitles,
        isDataLoader: true,
      },
      {
        thunkAction: fetchLanguageTurnerProtocols,
        actionKey: actionKeys.fetchLanguageTurnerProtocols,
        isDataLoader: true,
      },
      {
        thunkAction: createWorker,
        actionKey: actionMapForOtherLoaders.createWorker,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: updateWorker,
        actionKey: actionMapForOtherLoaders.updateWorker,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: searchWorker,
        actionKey: actionMapForOtherLoaders.searchWorker,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: createPassword,
        actionKey: actionMapForOtherLoaders.createPassword,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: acceptBiometricConsent,
        actionKey: actionMapForOtherLoaders.acceptBiometricConsent,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: uploadImageToS3,
        actionKey: actionMapForOtherLoaders.uploadImageToS3,
        isDataLoader: false,
      },
      {
        thunkAction: fetchWorkerDetails,
        actionKey: actionMapForOtherLoaders.fetchWorkerDetails,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: uploadGovernmentDocumentFiles,
        actionKey: actionMapForOtherLoaders.uploadGovernmentDocumentFiles,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: deleteGovernmentDocumentFile,
        actionKey: actionMapForOtherLoaders.deleteGovernmentDocumentFile,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: deleteCertification,
        actionKey: actionMapForOtherLoaders.deleteCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: addCertification,
        actionKey: actionMapForOtherLoaders.addCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: getCertificationDetails,
        actionKey: actionMapForOtherLoaders.getCertificationDetails,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: updateCertification,
        actionKey: actionMapForOtherLoaders.updateCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: addMyCertification,
        actionKey: actionMapForOtherLoaders.addMyCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: updateMyCertification,
        actionKey: actionMapForOtherLoaders.updateMyCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: deleteMyCertification,
        actionKey: actionMapForOtherLoaders.deleteMyCertification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: uploadSignatureToS3,
        actionKey: actionMapForOtherLoaders.uploadSignatureToS3,
        isDataLoader: false,
      },
      {
        thunkAction: acceptProject,
        actionKey: actionMapForOtherLoaders.acceptProject,
        isDataLoader: false,
      },
      {
        thunkAction: fetchAllCertifications,
        actionKey: actionMapForOtherLoaders.fetchAllCertifications,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchMyCertifications,
        actionKey: actionMapForOtherLoaders.fetchMyCertifications,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchWorkerDrugTestingSchedules,
        actionKey: actionMapForOtherLoaders.fetchWorkerDrugTestingSchedules,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: deleteWorkerThirdPartyDrugTestResult,
        actionKey: actionMapForOtherLoaders.deleteWorkerThirdPartyDrugTestResult,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: deleteWorkerDrugTestingSchedule,
        actionKey: actionMapForOtherLoaders.deleteWorkerDrugTestingSchedule,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchCertificationGroups,
        actionKey: actionMapForOtherLoaders.fetchCertificationGroups,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: sendPinForPiiVerification,
        actionKey: actionMapForOtherLoaders.sendPinForPiiVerification,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: verifyPiiPin,
        actionKey: actionMapForOtherLoaders.verifyPiiPin,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchConsentFormInformation,
        actionKey: actionMapForOtherLoaders.fetchConsentFormInformation,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: downloadConsentFormPdf,
        actionKey: actionMapForOtherLoaders.downloadConsentFormPdf,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchProfileCustomFieldsConfig,
        actionKey: actionMapForOtherLoaders.fetchProfileCustomFieldsConfig,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: changePassword,
        actionKey: actionMapForOtherLoaders.changePassword,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: assignExistingWorkerToCompanies,
        actionKey: actionMapForOtherLoaders.assignExistingWorkerToCompanies,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
      {
        thunkAction: fetchWorkerConsentAcceptances,
        actionKey: actionMapForOtherLoaders.fetchWorkerConsentAcceptances,
        isDataLoader: false,
        actionKeyAsLoaderKey: true,
      },
    ]),
});

export default loadingMapSlice;
